import { StateCreator } from 'zustand';
import { AsyncConfirmation, AsyncDialogProps, Dialog, DialogOptions } from './types';
import { BoundStoreState, useBoundStore } from './index';

export interface DialogSliceState {
	dialogs: Array<Dialog>;
	dialogOptions: Partial<Record<Dialog, DialogOptions>>;
	asyncDialogProps: AsyncDialogProps;
	asyncDialogResolvers?: {
		resolve: (value: boolean) => void;
		reject: () => void;
	};
	asyncConfirmation?: AsyncConfirmation;
}

const defaultAsyncDialogProps: AsyncDialogProps = {
  title: null,
  content: null,
  confirmLabel: null,
  cancelLabel: null,
  show: false,
};

export const createDialogSlice: StateCreator<
		BoundStoreState,
		[],
		[],
		DialogSliceState
> = () => ({
  dialogs: [],
  dialogOptions: {},
  asyncDialogProps: defaultAsyncDialogProps,
  asyncDialogResolvers: undefined,
  asyncConfirmation: undefined,
});

export const openDialog = (dialog: Dialog, options?: DialogOptions) => {
  const { dialogs, dialogOptions } = useBoundStore.getState();

  useBoundStore.setState({
    dialogs: [...dialogs, dialog],
    dialogOptions: {
      ...dialogOptions,
      [dialog]: options,
    },
  });
};

export const closeDialog = (dialog: Dialog) => {
  const { dialogs, dialogOptions } = useBoundStore.getState();

  const newDialogOptions = { ...dialogOptions };
  delete newDialogOptions[dialog];

  useBoundStore.setState({
    dialogs: dialogs.filter(d => d !== dialog),
    dialogOptions: newDialogOptions,
  });
};

export const setAsyncDialogProps = (props: AsyncDialogProps) => {
  useBoundStore.setState({ asyncDialogProps: props });
};

export const createAsyncConfirmation = (dictionary: any): AsyncConfirmation => {
  return ({ title, content, confirmLabel, cancelLabel }) => {
    return new Promise((resolve) => {
      useBoundStore.setState({
        asyncDialogProps: {
          show: true,
          title: title || dictionary.dialogs.defaultTitle,
          content: content || dictionary.dialogs.defaultContent,
          confirmLabel: confirmLabel ?? dictionary.dialogs.yes,
          cancelLabel: cancelLabel ?? dictionary.dialogs.no,
        },
        asyncDialogResolvers: {
          resolve,
          reject: () => resolve(false),
        },
      });
    });
  };
};

export const resolveAsyncConfirmation = (result: boolean) => {
  const { asyncDialogResolvers } = useBoundStore.getState();

  if (asyncDialogResolvers) {
    asyncDialogResolvers.resolve(result);
  }

  useBoundStore.setState({
    asyncDialogProps: defaultAsyncDialogProps,
    asyncDialogResolvers: undefined,
  });
};

export const useDialogs = () => useBoundStore(state => state.dialogs);
export const useDialogOptions = () => useBoundStore(state => state.dialogOptions);
export const useAsyncDialogProps = () => useBoundStore(state => state.asyncDialogProps);
export const useAsyncConfirmation = () => useBoundStore(state => state.asyncConfirmation);

export const useIsDialogOpen = (dialog: Dialog) =>
  useBoundStore(state => state.dialogs.includes(dialog));

export const useDialogOption = <T = any>(dialog: Dialog) =>
  useBoundStore(state => state.dialogOptions[dialog] as T | undefined);

export const useOpenedDialogs = () => {
  return useBoundStore(state => {
    const dialogs = state.dialogs;
    return Object.values(Dialog)
      .filter(k => typeof k === 'string')
      .reduce((prev, curr) => {
        return {
          ...prev,
          [curr]: dialogs.includes(curr as Dialog),
        };
      }, {} as Record<string, boolean>);
  });
};

export const useDialogContext = () => {
  const asyncConfirmation = useAsyncConfirmation();
  const dialogOptions = useDialogOptions();

  return {
    openDialog,
    closeDialog,
    asyncConfirmation: asyncConfirmation || (() => Promise.resolve(false)),
    dialogOptions,
  };
};

export const syncDialogsWithURL = () => {
  const params = new URLSearchParams(window.location.search);
  const dialogsFromURL = params.get('dialog')?.split(',').filter(Boolean) as Dialog[];

  if (dialogsFromURL?.length > 0) {
    useBoundStore.setState({ dialogs: dialogsFromURL });
  }

  return useBoundStore.subscribe(
    (state) => state.dialogs,
    (dialogs) => {
      const params = new URLSearchParams(window.location.search);

      if (dialogs.length > 0) {
        params.set('dialog', dialogs.join(','));
      } else {
        params.delete('dialog');
      }

      const newURL = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
      window.history.replaceState({}, '', newURL);
    },
  );
};

export const setAsyncConfirmation = (dictionary: any) => {
  const asyncConfirmation = createAsyncConfirmation(dictionary);
  useBoundStore.setState({ asyncConfirmation });
};